import Splide from '@splidejs/splide';
import { pixelBreakpoints } from '../../helpers/breakpoints';

/**
 * Product-card colour swatches: Splide carousel with responsive overflow behaviour.
 *
 * Uses Splide overflow API — see https://splidejs.com/guides/overflow/
 *
 * @export
 * @class ProductSwatches
 */
export default class ProductSwatches {
  /**
   * @param {HTMLElement} container - Wrapper with `[data-product-item-swatches]`
   */
  constructor(container) {
    const root = container?.querySelector(
      '[data-product-item-swatches-splide]',
    );
    if (!root) {
      return;
    }

    this.root = root;

    this._mqMdUp = window.matchMedia(`(min-width: ${pixelBreakpoints.md})`);

    this._syncOverflowOptions = this._syncOverflowOptions.bind(this);
    this._onBreakpointCross = this._onBreakpointCross.bind(this);

    const startIndex = this._getSelectedSlideIndex();

    this.splide = new Splide(root, {
      type: 'slide',
      arrows: false,
      pagination: false,
      autoWidth: true,
      drag: false,
      rewind: false,
      start: startIndex,
    });

    /** Must subscribe before {@link Splide.mount} — see Splide overflow guide */
    this.splide.on('overflow', this._syncOverflowOptions);

    this.splide.on('mounted', () => {
      const index = this._getSelectedSlideIndex();
      if (this.splide.index !== index) {
        this.splide.go(index);
      }
    });

    this.splide.mount();

    this._mqMdUp.addEventListener('change', this._onBreakpointCross);

    requestAnimationFrame(() => this._primeOverflowOnce());
  }

  /**
   * Index of the slide for the checked colour swatch (Splide `is-active` slide).
   * @returns {number}
   */
  _getSelectedSlideIndex() {
    const slides = [...this.root.querySelectorAll('.splide__slide')];
    const index = slides.findIndex(
      slide =>
        slide.hasAttribute('data-swatch-selected') ||
        slide.querySelector('[data-product-item-swatch]:checked'),
    );

    return index >= 0 ? index : 0;
  }

  /**
   * @param {boolean} [isOverflow] - Passed from Splide overflow event; omit to read Layout API
   */
  _syncOverflowOptions(isOverflow) {
    if (!this.splide?.Components?.Layout) {
      return;
    }

    const overflow =
      typeof isOverflow === 'boolean'
        ? isOverflow
        : Boolean(this.splide.Components.Layout.isOverflow());

    const mdUp = this._mqMdUp.matches;

    this.splide.options = {
      pagination: false,
      arrows: mdUp && overflow,
      drag: overflow,
    };
  }

  _onBreakpointCross() {
    requestAnimationFrame(() => this._syncOverflowOptions(undefined));
  }

  /** If the initial overflow event raced mount, prime options from Layout */
  _primeOverflowOnce() {
    if (!this.splide?.Components?.Layout) {
      return;
    }

    try {
      this._syncOverflowOptions(this.splide.Components.Layout.isOverflow?.());
    } catch {
      /* noop */
    }
  }

  destroy() {
    this._mqMdUp.removeEventListener('change', this._onBreakpointCross);
    this._mqMdUp = null;

    if (this.splide) {
      this.splide.destroy();
      this.splide = null;
    }

    this.root = null;
  }
}
